import axios, { AxiosError, AxiosResponse } from "axios";
import { v4 as uuidv4 } from "uuid";

import { BaseApiService } from "../base/base-api.service";

import { ProviderDTO } from "../../models/providers";
import { ProviderSTO } from "../../models/providers";
import { ProviderLogoFilter } from "../../models/providers";
import { ListSortDirection, Sort } from "../../models/api/sort";
import { SaveResponse } from "../../models/api/save";
import { PropsFilter } from "../../models/api/props-filter";
import { Page } from "../../models/api/page";
import { DownloadedDocumentFile } from "../../models/documents/document-files";

import { getAuthToken } from "../../custom-hooks/useDecodedToken/useDecodedToken";

export class ProvidersApiService extends BaseApiService<ProviderDTO, ProviderSTO> {
  constructor() {
    super({
      servicePath: "/Providers",
      loadItemsPath: "/GetProviders",
      saveItemsPath: "/SaveProviders",
    });
    this.defaultSort = [
      {
        name: "name",
        direction: ListSortDirection.Ascending,
      } as Sort,
    ];
  }

  public async saveLogoForm(formData: FormData, timeout = this.defaultTimeout): Promise<SaveResponse> {
    const request = this.createSaveRequest([], timeout);
    const token = getAuthToken();
    const url = `${this.apiURL}${this.apiPaths.servicePath}/SaveProviderLogo`;

    formData.append("requestId", request.id);
    formData.append("invokerId", request.invokerId ?? uuidv4());
    formData.append("correlationId", request.correlationId ?? uuidv4());
    formData.append("timeout", "60");

    let response = {
      data: { isSucceeded: false } as SaveResponse,
    };
    try {
      response = await axios.post<SaveResponse>(url, formData, {
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch (err) {
      const error = err as AxiosError;
      let errorMessage = error?.response?.data?.error ? error?.response?.data?.error : error?.message ?? error;

      if (response.data) {
        response.data.error = errorMessage;
      }

      console.log("Failure saving file. ", errorMessage);

      switch (error?.response?.status) {
        case 400: {
          errorMessage = "Bad request/wrong data sent.";
          break;
        }
        case 401: {
          errorMessage = "Not authenticated. Try to sign in again.";
          break;
        }
        case 403: {
          errorMessage = "Not authorized. Review your access rights.";
          break;
        }
        case 404: {
          errorMessage = "Data not found.";
          break;
        }

        case 500: {
          errorMessage = "Failure saving file - Server error.";
          break;
        }

        case 502: {
          errorMessage = "Bad gateway / endpoint unreachable.";
          break;
        }

        case 503: {
          errorMessage = "Service unavailable.";
          break;
        }

        default:
          break;
      }
    }

    return response?.data;
  }

  /**
   * Download logo file contents for provider
   * @param providerId
   */
  public async loadProviderLogoBlob(providerId: string, signal?: AbortSignal): Promise<DownloadedDocumentFile> {
    const filter: string = "";
    const propsFilter: ProviderLogoFilter = {
      ...this.defaultPropsFilter,
      providerId,
    };
    const sorts: Sort[] = [];
    const page: Page = { pageNumber: 0, pageSize: 1 };
    const exactMatch = false;
    const timeout = this.defaultTimeout;
    const request = this.createLoadRequest(propsFilter, filter, sorts, page, exactMatch, timeout);
    const token = getAuthToken();
    const url = `${this.apiURL}${this.apiPaths.servicePath}/GetProviderLogoContents`;

    let response = {
      data: new Blob(),
      "content-disposition": "",
    };
    let errorMessage = "";
    try {
      let result = await axios.post<Blob>(url, request, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
        signal,
      });

      response.data = result.data;
      response["content-disposition"] = result.headers["content-disposition"] ?? "";
    } catch (err) {
      const error = err as AxiosError;
      errorMessage = error?.response?.data?.error ? error?.response?.data?.error : error?.message ?? error;

      console.log((error?.response?.status ?? "") + ": Failure loading file. ", errorMessage);

      switch (error?.response?.status) {
        case 400: {
          errorMessage = "Bad request/wrong data sent.";
          break;
        }
        case 401: {
          errorMessage = "Not authenticated. Try to sign in again.";
          break;
        }
        case 403: {
          errorMessage = "Not authorized. Review your access rights.";
          break;
        }
        case 404: {
          errorMessage = "Data not found.";
          break;
        }

        case 500: {
          errorMessage = "Failure loading file - Server error.";
          break;
        }

        case 502: {
          errorMessage = "Bad gateway / endpoint unreachable.";
          break;
        }

        case 503: {
          errorMessage = "Service unavailable.";
          break;
        }

        default:
          break;
      }
    }

    const { data } = response;
    const contentDispositionHeader = response["content-disposition"];
    const fileName = this.getFileName(contentDispositionHeader);

    return { name: fileName, data, error: errorMessage };
  }

  /**
   * Download logo file contents for current provider
   * @param id
   */
  public async loadCurrentProviderLogoBlob(signal?: AbortSignal): Promise<DownloadedDocumentFile> {
    const filter: string = "";
    const propsFilter: PropsFilter = this.defaultPropsFilter;
    const sorts: Sort[] = [];
    const page: Page = { pageNumber: 0, pageSize: 1 };
    const exactMatch = false;
    const timeout = this.defaultTimeout;
    const request = this.createLoadRequest(propsFilter, filter, sorts, page, exactMatch, timeout);

    const url = `${this.apiURL}${this.apiPaths.servicePath}/GetCurrentProviderLogoContents`;

    let response: AxiosResponse<Blob, any> = {
      data: new Blob(),
      status: 200,
      statusText: "OK",
      headers: {},
      config: {},
    };
    let errorMessage = "";
    try {
      response = await axios.post<Blob>(url, request, {
        headers: {}, // for current provider site data do not use authorization header
        responseType: "blob",
        signal,
      });
    } catch (err) {
      const error = err as AxiosError;
      errorMessage = error?.response?.data?.error ? error?.response?.data?.error : error?.message ?? error;

      console.log((error?.response?.status ?? "") + ": Failure loading file. ", errorMessage);

      switch (error?.response?.status) {
        case 400: {
          errorMessage = "Bad request/wrong data sent.";
          break;
        }
        case 401: {
          errorMessage = "Not authenticated. Try to sign in again.";
          break;
        }
        case 403: {
          errorMessage = "Not authorized. Review your access rights.";
          break;
        }
        case 404: {
          errorMessage = "Data not found.";
          break;
        }

        case 500: {
          errorMessage = "Failure loading file - Server error.";
          break;
        }

        case 502: {
          errorMessage = "Bad gateway / endpoint unreachable.";
          break;
        }

        case 503: {
          errorMessage = "Service unavailable.";
          break;
        }

        default:
          break;
      }
    }

    const { data, headers } = response;
    const contentDispositionHeader = headers["content-disposition"] ?? "";
    const fileName = this.getFileName(contentDispositionHeader);

    return { name: fileName, data, error: errorMessage };
  }

  private getFileName(contentDispositionHeader: string): string {
    const fileNameUtf8Attr = "filename*=UTF-8''";
    const fileNameAttr = "filename=";
    let fileName = "";
    if (contentDispositionHeader.indexOf(fileNameUtf8Attr) !== -1) {
      fileName = contentDispositionHeader.split(fileNameUtf8Attr)[1].split(";")[0];
    } else if (contentDispositionHeader.indexOf(fileNameAttr) !== -1) {
      fileName = contentDispositionHeader.split(fileNameAttr)[1].split(";")[0];
    }

    return fileName;
  }
}
